import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Download } from "lucide-react";

const YEARS = [2024, 2025, 2026, 2027];
const QUARTERS = ["t1", "t2", "t3", "t4"];

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

async function handleLogout() {
  try {
    await fetch("https://desarrollandoterritorios.com/api/auth/logout/", {
      method: "POST",
      credentials: "include",
    });
  } catch (e) {
    console.error(e);
  } finally {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    sessionStorage.clear();
    window.location.href = "/login";
  }
}

export default function SeguimientoPDDGlobal() {
  const navigate = useNavigate();
  const [secretarias, setSecretarias]   = useState([]);
  const [expandida, setExpandida]       = useState(null);
  const [indicadores, setIndicadores]   = useState({});
  const [loadingDep, setLoadingDep]     = useState({});
  const [loadingPage, setLoadingPage]   = useState(true);
  const [error, setError]               = useState("");
  const [exportando, setExportando]     = useState(false);

  // ── Cargar lista de secretarías ─────────────────────────────────
  useEffect(() => {
    const fetchSecretarias = async () => {
      try {
        const resp = await fetch(
          "https://desarrollandoterritorios.com/api/pdd_secretarias.php",
          { credentials: "include" }
        );
        const json = await resp.json();
        if (json.ok) setSecretarias(json.data || []);
        else setError(json.message || "Error cargando secretarías");
      } catch {
        setError("No se pudo conectar con el servidor");
      } finally {
        setLoadingPage(false);
      }
    };
    fetchSecretarias();
  }, []);

  // ── Cargar indicadores de una secretaría al expandir ────────────
  const cargarIndicadores = useCallback(async (dependencia) => {
    if (indicadores[dependencia]) return;
    setLoadingDep((p) => ({ ...p, [dependencia]: true }));
    try {
      const resp = await fetch(
        `https://desarrollandoterritorios.com/api/pdd_actividades_global.php?dependencia=${encodeURIComponent(dependencia)}`,
        { credentials: "include" }
      );
      const json = await resp.json();
      setIndicadores((p) => ({ ...p, [dependencia]: json.data || [] }));
    } catch {
      setIndicadores((p) => ({ ...p, [dependencia]: [] }));
    } finally {
      setLoadingDep((p) => ({ ...p, [dependencia]: false }));
    }
  }, [indicadores]);

  const toggleSecretaria = (dep) => {
    if (expandida === dep) {
      setExpandida(null);
    } else {
      setExpandida(dep);
      cargarIndicadores(dep);
    }
  };

  // ── Cálculos ────────────────────────────────────────────────────
  const totalAnual = (item, year) =>
    QUARTERS.reduce((acc, q) => acc + toNum(item[`${year}_${q}`]), 0);

  const avancePct = (item) => {
    const total = YEARS.reduce((a, y) => a + totalAnual(item, y), 0);
    const meta = toNum(item.meta_cuatrenio);
    if (!meta) return 0;
    return Math.min(Math.round((total / meta) * 100), 100);
  };

  // ── Exportar Excel (SheetJS) ─────────────────────────────────────
  const exportarExcel = async () => {
    setExportando(true);
    try {
      // Cargar todas las secretarías que no se hayan cargado aún
      const pendientes = secretarias.filter((s) => !indicadores[s.dependencia]);
      await Promise.all(
        pendientes.map(async (s) => {
          const resp = await fetch(
            `https://desarrollandoterritorios.com/api/pdd_actividades_global.php?dependencia=${encodeURIComponent(s.dependencia)}`,
            { credentials: "include" }
          );
          const json = await resp.json();
          setIndicadores((p) => ({ ...p, [s.dependencia]: json.data || [] }));
          return json.data || [];
        })
      );

      // Esperar a que el estado se actualice
      await new Promise((r) => setTimeout(r, 300));

      // Construir filas para el Excel
      const rows = [["Secretaría", "Indicador", "Nombre", "Unidad", "Meta Cuatrienio",
        "2024 T1","2024 T2","2024 T3","2024 T4","Total 2024",
        "2025 T1","2025 T2","2025 T3","2025 T4","Total 2025",
        "2026 T1","2026 T2","2026 T3","2026 T4","Total 2026",
        "2027 T1","2027 T2","2027 T3","2027 T4","Total 2027",
        "Total Ejecutado","% Avance"]];

      // Usar el estado actual más los datos recién cargados
      const todosLosIndicadores = { ...indicadores };
      pendientes.forEach(async (s) => {
        if (!todosLosIndicadores[s.dependencia]) {
          const resp = await fetch(
            `https://desarrollandoterritorios.com/api/pdd_actividades_global.php?dependencia=${encodeURIComponent(s.dependencia)}`,
            { credentials: "include" }
          );
          const json = await resp.json();
          todosLosIndicadores[s.dependencia] = json.data || [];
        }
      });

      secretarias.forEach((sec) => {
        const items = todosLosIndicadores[sec.dependencia] || [];
        items.forEach((item) => {
          const fila = [
            sec.dependencia,
            item.numero_indicador,
            item.nombre_resumido || item.nombre_completo,
            item.unidad_medida,
            toNum(item.meta_cuatrenio),
          ];
          YEARS.forEach((year) => {
            QUARTERS.forEach((q) => fila.push(toNum(item[`${year}_${q}`])));
            fila.push(totalAnual(item, year));
          });
          const totalEjec = YEARS.reduce((a, y) => a + totalAnual(item, y), 0);
          const meta = toNum(item.meta_cuatrenio);
          fila.push(totalEjec);
          fila.push(meta ? Math.round((totalEjec / meta) * 100) + "%" : "0%");
          rows.push(fila);
        });
      });

      // Generar Excel con SheetJS
      const XLSX = await import("https://cdn.jsdelivr.net/npm/xlsx@0.18.5/+esm");
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = rows[0].map((_, i) => ({ wch: i < 3 ? 30 : 12 }));
      XLSX.utils.book_append_sheet(wb, ws, "Seguimiento PDD");
      XLSX.writeFile(wb, `SeguimientoPDD_${new Date().toISOString().slice(0,10)}.xlsx`);
    } catch (e) {
      alert("Error generando el Excel: " + e.message);
    } finally {
      setExportando(false);
    }
  };

  if (loadingPage) {
    return (
      <div style={s.page}>
        <div style={s.wrapper}>
          <div style={s.emptyMsg}>Cargando secretarías...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.wrapper}>

        {/* Header */}
        <div style={s.topBar}>
          <div>
            <h1 style={s.title}>Seguimiento Plan de Desarrollo</h1>
            <p style={s.subtitle}>Vista global — todas las secretarías</p>
          </div>
          <button type="button" onClick={handleLogout} style={s.logoutBtn}>
            Cerrar sesión
          </button>
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        {/* Tarjeta 1 — Acordeón por secretaría */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Plan de acción por secretaría</span>
            <span style={s.cardBadge}>{secretarias.length} secretarías</span>
          </div>

          <div style={s.acordeon}>
            {secretarias.map((sec) => {
              const dep      = sec.dependencia;
              const abierta  = expandida === dep;
              const items    = indicadores[dep] || [];
              const cargando = loadingDep[dep];

              return (
                <div key={dep} style={s.seccion}>
                  <button style={s.seccionBtn} onClick={() => toggleSecretaria(dep)}>
                    <div style={s.seccionLeft}>
                      {abierta
                        ? <ChevronDown size={18} color="#1f3b64" />
                        : <ChevronRight size={18} color="#1f3b64" />
                      }
                      <span style={s.seccionNombre}>{dep}</span>
                    </div>
                    <span style={s.seccionMeta}>
                      {sec.total_indicadores} indicador{sec.total_indicadores !== 1 ? "es" : ""}
                    </span>
                  </button>

                  {abierta && (
                    <div style={s.seccionBody}>
                      {cargando && <div style={s.emptyMsg}>Cargando indicadores...</div>}
                      {!cargando && items.length === 0 && (
                        <div style={s.emptyMsg}>No hay indicadores registrados.</div>
                      )}
                      {!cargando && items.length > 0 && (
                        <div style={{ overflowX: "auto" }}>
                          <table style={s.table}>
                            <thead>
                              <tr style={s.thead}>
                                <th style={s.th}>Indicador</th>
                                <th style={s.th}>Meta cuatrienio</th>
                                {YEARS.map((y) => (
                                  <th key={y} style={s.th}>{y}</th>
                                ))}
                                <th style={s.th}>Avance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item) => {
                                const pct = avancePct(item);
                                return (
                                  <tr key={item.id} style={s.tr}>
                                    <td style={s.tdInd}>
                                      <strong style={{cursor:"pointer",color:"#1677c8"}} onClick={()=>navigate(`/revision-pdd/indicador/${item.id}`)}>{item.numero_indicador}</strong>
                                      <div style={s.indNombre}>
                                        {item.nombre_resumido || item.nombre_completo}
                                      </div>
                                    </td>
                                    <td style={s.tdC}>{toNum(item.meta_cuatrenio)}</td>
                                    {YEARS.map((year) => (
                                      <td key={year} style={s.tdC}>
                                        <div style={s.yearCell}>
                                          {QUARTERS.map((q) => (
                                            <div key={q} style={s.qRow}>
                                              <span style={s.qLabel}>{q.toUpperCase()}</span>
                                              <span style={s.qVal}>{toNum(item[`${year}_${q}`])}</span>
                                            </div>
                                          ))}
                                          <div style={s.yearTotal}>
                                            Total: {totalAnual(item, year)}
                                          </div>
                                        </div>
                                      </td>
                                    ))}
                                    <td style={s.tdProg}>
                                      <div style={s.pctText}>{pct}%</div>
                                      <div style={s.barBg}>
                                        <div style={{ ...s.barFill, width: `${pct}%` }} />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Tarjeta 2 — Descarga Excel */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Exportar seguimiento completo</span>
          </div>
          <div style={s.exportBody}>
            <p style={s.exportDesc}>
              Descarga el seguimiento de todas las secretarías con los valores por trimestre,
              totales anuales y porcentaje de avance vs meta cuatrienio.
            </p>
            <button
              type="button"
              onClick={exportarExcel}
              disabled={exportando}
              style={{ ...s.exportBtn, ...(exportando ? s.exportBtnDisabled : {}) }}
            >
              <Download size={16} style={{ marginRight: 8 }} />
              {exportando ? "Generando Excel..." : "Descargar Excel"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

const s = {
  page:             { minHeight: "100vh", background: "#f5f7fb", padding: "24px" },
  wrapper:          { maxWidth: "1400px", margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 },
  topBar:           { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  title:            { margin: 0, fontSize: 30, fontWeight: 800, color: "#1f3b64" },
  subtitle:         { marginTop: 4, color: "#5b6472", fontSize: 15 },
  logoutBtn:        { border: "none", borderRadius: 10, background: "#ef4444", color: "#fff", padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" },
  errorBox:         { background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: "12px 16px", fontSize: 14 },
  card:             { background: "#fff", borderRadius: 16, boxShadow: "0 4px 18px rgba(0,0,0,0.06)", overflow: "hidden" },
  cardHeader:       { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #f1f5f9" },
  cardTitle:        { fontSize: 16, fontWeight: 700, color: "#1f3b64" },
  cardBadge:        { fontSize: 12, background: "#e0f2fe", color: "#0369a1", borderRadius: 999, padding: "4px 10px", fontWeight: 600 },
  acordeon:         { display: "flex", flexDirection: "column" },
  seccion:          { borderBottom: "1px solid #f1f5f9" },
  seccionBtn:       { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left" },
  seccionLeft:      { display: "flex", alignItems: "center", gap: 10 },
  seccionNombre:    { fontSize: 15, fontWeight: 700, color: "#1f3b64" },
  seccionMeta:      { fontSize: 13, color: "#6b7280" },
  seccionBody:      { padding: "0 16px 16px" },
  emptyMsg:         { textAlign: "center", padding: 20, color: "#64748b", fontSize: 14 },
  table:            { width: "100%", borderCollapse: "collapse", minWidth: 900 },
  thead:            { background: "#1677c8" },
  th:               { color: "#fff", padding: "12px 10px", textAlign: "center", fontWeight: 700, fontSize: 13, border: "1px solid #d9e2f1" },
  tr:               { background: "#fff" },
  tdInd:            { border: "1px solid #e2e8f0", padding: "12px 10px", verticalAlign: "top", minWidth: 260 },
  indNombre:        { marginTop: 4, color: "#475569", fontSize: 13, lineHeight: 1.4 },
  tdC:              { border: "1px solid #e2e8f0", padding: "8px", textAlign: "center", verticalAlign: "top" },
  yearCell:         { display: "flex", flexDirection: "column", gap: 2 },
  qRow:             { display: "flex", justifyContent: "space-between", fontSize: 12, gap: 6 },
  qLabel:           { color: "#94a3b8", fontWeight: 600 },
  qVal:             { color: "#1e293b", fontWeight: 500 },
  yearTotal:        { borderTop: "1px solid #e2e8f0", marginTop: 4, paddingTop: 4, fontSize: 12, fontWeight: 700, color: "#1f3b64", textAlign: "right" },
  tdProg:           { border: "1px solid #e2e8f0", padding: "10px 12px", minWidth: 100, verticalAlign: "middle" },
  pctText:          { fontWeight: 700, textAlign: "center", marginBottom: 6, fontSize: 14 },
  barBg:            { height: 8, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" },
  barFill:          { height: 8, background: "#16a34a", borderRadius: 999 },
  exportBody:       { padding: "20px" },
  exportDesc:       { fontSize: 14, color: "#475569", marginBottom: 16, lineHeight: 1.6 },
  exportBtn:        { display: "inline-flex", alignItems: "center", border: "none", borderRadius: 10, background: "#16a34a", color: "#fff", padding: "12px 24px", cursor: "pointer", fontWeight: 700, fontSize: 14 },
  exportBtnDisabled:{ background: "#94a3b8", cursor: "not-allowed" },
};